import { byIdsResultJSONZ, ByIdsResultJSON } from "_shared/_api/by-ids/by-ids-result";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { listCountsHook, listCountsTags } from "_shared/_api/list-counts/list-counts-hook";
import { api } from "_shared/_stores/api";
import { calendarEventListTag } from "calendar/_api/calendar-event-api";
import type { RootState } from "_shared/_stores/root-reducer";
import type { AppDispatch } from "_shared/_stores/store-types";
import EntityMember from "_shared/entity-members/entity-member";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Session from "users/__session/session";
import {
  serializeQueryArgsBySessionView,
  serializeQueryArgsWithoutSession,
} from "users/__session/session-query-cache-keys";
import { ProposalContext } from "work/proposals/__constants/proposal-lifecycle";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import { WorkAgreementOrderField } from "work/work-agreements/_api/queries/_fields/work-agreement-order-field";
import WorkAgreementQuery from "work/work-agreements/_api/queries/work-agreement-query";
import {
  WorkAgreementListCountsJSONZ,
  type WorkAgreementListCounts,
} from "work/work-agreements/_api/queries/_schemas/work-agreement-list-counts-schema";
import BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import { workAgreementVersionHeader } from "work/work-agreements/_api/work-agreement-version-header";
import { WorkAgreementJSON, WorkAgreementJSONZ } from "work/work-agreements/_schemas/work-agreement-schema";
import { InFlightRevisionDisposition, WorkTeamSide } from "work/work-agreements/teams/leader-handover/team-leader-handover";
import VendorTeamSchema, { VendorTeamJSON } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import WorkAgreement from "work/work-agreements/work-agreement";
import z from "zod";

type WorkAgreementApiCacheContext = {
  dispatch: AppDispatch;
  getState: () => RootState;
  session: Session;
};

type WorkAgreementUpsertCacheContext = WorkAgreementApiCacheContext & {
  previousWorkAgreementJSON?: WorkAgreementJSON | null;
  workAgreementJSON: WorkAgreementJSON;
};

type WorkAgreementRemoveCacheContext = WorkAgreementApiCacheContext & {
  previousWorkAgreementJSON?: WorkAgreementJSON | null;
  workAgreementIdValue: string;
};

export const invalidateWorkAgreementListApiCaches = (dispatch: AppDispatch) => {
  dispatch(
    workAgreementsApi.util.invalidateTags([
      { type: "WorkAgreement", id: "LIST" },
      { type: "WorkAgreement", id: "ActiveCount" },
      { type: "WorkAgreement", id: "ArchiveCount" },
    ]),
  );
};

/**
 * Whether a list row is the work as it is, and so may stand in for the agreement's own read. A row
 * with `frozenAt` is the view the reader kept of work they left: their record of it, not the
 * agreement. Opening it asks the server, which answers with the work as it is to a reader who can
 * still open it (#7089 review).
 */
export const isTheWorkAsItIs = (workAgreementJSON: WorkAgreementJSON) => !workAgreementJSON.frozenAt;

export const upsertWorkAgreementApiCaches = ({
  dispatch,
  session,
  workAgreementJSON,
}: WorkAgreementUpsertCacheContext) => {
  if (!workAgreementJSON.id) {
    throw new Error("Cannot cache work agreement without an id.");
  }

  dispatch(
    workAgreementsApi.util.upsertQueryEntries([
      {
        endpointName: "getWorkAgreementById",
        arg: { workAgreementId: workAgreementJSON.id, session },
        value: workAgreementJSON,
      },
    ]),
  );
  invalidateWorkAgreementListApiCaches(dispatch);
};

export const removeWorkAgreementApiCaches = ({
  dispatch,
  session,
  workAgreementIdValue,
}: WorkAgreementRemoveCacheContext) => {
  dispatch(
    workAgreementsApi.util.upsertQueryEntries([
      {
        endpointName: "getWorkAgreementById",
        arg: { workAgreementId: workAgreementIdValue, session },
        value: null,
      },
    ]),
  );
  invalidateWorkAgreementListApiCaches(dispatch);
};

export const invalidateWorkAgreementByIdApiCache = ({
  dispatch,
  workAgreementIdValue,
}: Pick<WorkAgreementApiCacheContext, "dispatch"> & { workAgreementIdValue: string }) => {
  dispatch(workAgreementsApi.util.invalidateTags([{ type: "WorkAgreement", id: workAgreementIdValue }]));
};

/**
 * Asks again for what a change to who is on the work changes about proposals: the proposals the
 * reader has left, and the proposal the agreement came from, which a leaver now reads as they left it
 * and somebody brought back reads as it is (#5833).
 */
export const invalidateProposalsAWorkRosterChangeMoves = ({
  dispatch,
  sourceProposalIdValue,
}: Pick<WorkAgreementApiCacheContext, "dispatch"> & { sourceProposalIdValue?: string }) => {
  dispatch(
    workAgreementsApi.util.invalidateTags([
      { type: "Proposal", id: "LEFT" },
      ...(sourceProposalIdValue ? [{ type: "Proposal" as const, id: sourceProposalIdValue }] : []),
    ]),
  );
};

export const workAgreementsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getWorkAgreements: build.query<Paginated<WorkAgreementJSON>, { query: WorkAgreementQuery; interfaceName: string }>({
      query: ({ query }) => ({
        url: "work",
        params: query?.searchParams,
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        try {
          const { data: workAgreementsResponse } = await queryFulfilled;
          const session = Session.loadFromStorage(() => {});
          const entries = workAgreementsResponse.data
            .filter((workAgreementJSON): workAgreementJSON is WorkAgreementJSON & { id: string } =>
              Boolean(workAgreementJSON.id) && isTheWorkAsItIs(workAgreementJSON)
            )
            .map((workAgreementJSON) => ({
              endpointName: "getWorkAgreementById" as const,
              arg: { workAgreementId: workAgreementJSON.id, session },
              value: workAgreementJSON,
            }));
          if (entries.length > 0) {
            dispatch(workAgreementsApi.util.upsertQueryEntries(entries));
          }
        } catch {
          // Failed list requests have no rows to hydrate into the per-work-agreement cache.
        }
      },
      transformResponse: (response: WorkAgreementJSON[], meta): Paginated<WorkAgreementJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((item) => ({
                type: "WorkAgreement" as const,
                id: item.id ?? undefined,
              })),
              { type: "WorkAgreement" as const, id: "LIST" },
            ]
          : [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getWorkAgreementById: build.query<WorkAgreementJSON | null, { workAgreementId: string; session: Session }>({
      query: ({ workAgreementId }) => `work/${workAgreementId}`,
      serializeQueryArgs: serializeQueryArgsWithoutSession,
      rawResponseSchema: WorkAgreementJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result, error, request) => [{ type: "WorkAgreement", id: request.workAgreementId }],
    }),
    // Every id asked about is answered: an agreement in `found`, or an `unavailable` entry saying
    // whether it was refused or names nothing (#6122).
    getWorkAgreementsByIds: build.query<
      ByIdsResultJSON<BarebonesWorkAgreementResponse>,
      { workAgreementIds: string[] }
    >({
      query: ({ workAgreementIds }) => ({
        url: "work/get-by-ids",
        method: "POST",
        body: workAgreementIds,
      }),
      rawResponseSchema: byIdsResultJSONZ(z.looseObject({ id: z.string() })),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) =>
        result
          ? [
              ...result.found.map((item) => ({
                type: "WorkAgreement" as const,
                id: item.id ?? undefined,
              })),
              { type: "WorkAgreement" as const, id: "LIST" },
            ]
          : [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getWorkTeamsByWorkAgreementId: build.query<VendorTeamJSON[], { workAgreementId: string }>({
      query: ({ workAgreementId }) => `work/${workAgreementId}/teams`,
      rawResponseSchema: z.array(VendorTeamSchema.VendorTeamJSONZ),
      onSchemaFailure: logSchemaFailure,
    }),
    // #endregion

    // #region Counts
    /**
     * The most recently started active agreements, in the same context the active count is taken in.
     *
     * Deliberately not built on `WorkAgreementQuery`: that object carries no `context`, so a list
     * built from it answers a different question from `getActiveWorkAgreementsCount` and a card
     * showing both would put a total next to rows that are not drawn from it. The parameters here
     * are the count's parameters with a page asked for instead of a page size of zero.
     *
     * A picker that has to reach every agreement, not only the most recent, asks for a later page
     * and narrows by `name`, which the server matches anywhere in the name, ignoring case.
     */
    getActiveWorkAgreements: build.query<
      Paginated<WorkAgreementJSON>,
      { session: Session; pageSize: number; pageIndex?: number; name?: string }
    >({
      query: ({ session, pageSize, pageIndex, name }) => {
        const params = new URLSearchParams({
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          orderBy: WorkAgreementOrderField.Start,
          orderDir: OrderDirection.Descending,
          pageIndex: String(pageIndex ?? 0),
          pageSize: String(pageSize),
        });
        if (name) params.set("name", name);
        return { url: "work", params };
      },
      serializeQueryArgs: serializeQueryArgsBySessionView,
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: WorkAgreementJSON[], meta): Paginated<WorkAgreementJSON> => ({
        data: response,
        headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
      }),
      providesTags: [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getActiveWorkAgreementsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work",
        params: new URLSearchParams({
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      serializeQueryArgs: serializeQueryArgsBySessionView,
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "WorkAgreement" as const, id: "ActiveCount" }],
    }),
    /**
     * How many archived agreements the Archived tab lists. The server counts the current version of
     * each work alone unless asked, as it lists them (#6810), so the badge asks for the earlier
     * versions too exactly when the list does, and agrees with the rows under it.
     */
    getArchivedWorkAgreementsCount: build.query<number, { session: Session; includesEarlierVersions?: boolean }>({
      query: ({ session, includesEarlierVersions }) => ({
        url: "work",
        params: new URLSearchParams({
          isArchived: "true",
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
          // `WorkAgreementFilterParameters.currentVersionOnly`, which the list sends under its own name.
          ...(includesEarlierVersions ? { currentVersionOnly: "false" } : {}),
        }),
      }),
      serializeQueryArgs: serializeQueryArgsBySessionView,
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "WorkAgreement" as const, id: "ArchiveCount" }],
    }),
    /**
     * The work agreement list's counts, current and archived, in one request under the list's own
     * criteria (#6709). `criteria` is the list's filter as search parameters, less the archived
     * state, which the counts split on.
     */
    getWorkAgreementListCounts: build.query<WorkAgreementListCounts, { criteria: string }>({
      query: ({ criteria }) => `work/counts${criteria ? `?${criteria}` : ""}`,
      rawResponseSchema: WorkAgreementListCountsJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: listCountsTags("WorkAgreement", "ActiveCount", "ArchiveCount"),
    }),
    // #endregion

    // #region End Work Agreement
    endWorkAgreement: build.mutation<WorkAgreementJSON, { workAgreementId: string; version: number }>({
      query: ({ workAgreementId, version }) => ({
        url: `work/${workAgreementId}/end-agreement`,
        method: "POST",
        body: {},
        headers: workAgreementVersionHeader(version),
      }),
      rawResponseSchema: WorkAgreementJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (args, { dispatch, getState, queryFulfilled }) => {
        const session = Session.loadFromStorage(() => {});
        try {
          const { data: workAgreementJSON } = await queryFulfilled;
          upsertWorkAgreementApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            session,
            workAgreementJSON,
          });
        } catch {
          // Failed mutations have no work agreement data to hydrate.
        }
      },
      invalidatesTags: [calendarEventListTag],
    }),
    // #endregion

    // #region Revise Work Agreement
    reviseWorkAgreement: build.mutation<ProposalJSON, { workAgreement: WorkAgreement; session: Session }>({
      query: ({ workAgreement, session }) => {
        if (!session.user) {
          throw new Error("Cannot revise work agreement without a user.");
        }
        if (!workAgreement.id) {
          throw new Error("Cannot revise work agreement without id.");
        }
        return {
          url: `work/${workAgreement.id.value}/revise`,
          method: "POST",
          body: workAgreement.id.value,
          headers: workAgreementVersionHeader(workAgreement.version),
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.workAgreement.id?.value },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion

    // #region Quit Team
    quitWorkTeam: build.mutation<
      ProposalJSON | null,
      { workId: string; version: number; sourceProposalId?: string }
    >({
      query: ({ workId, version }) => ({
        url: `work/${workId}/teams/quit`,
        method: "POST",
        body: {},
        headers: workAgreementVersionHeader(version),
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          // Asked for again rather than dropped: the member keeps the work as it was when they left
          // (#5833), and that view is what the page they quit from shows next.
          invalidateWorkAgreementListApiCaches(dispatch as AppDispatch);
          invalidateWorkAgreementByIdApiCache({ dispatch: dispatch as AppDispatch, workAgreementIdValue: args.workId });
          invalidateProposalsAWorkRosterChangeMoves({
            dispatch: dispatch as AppDispatch,
            sourceProposalIdValue: args.sourceProposalId,
          });
        } catch {
          // Failed mutations should leave the local work-agreement caches untouched.
        }
      },
      invalidatesTags: (_result, _error) => [
        { type: "Proposal", id: "LIST" },
        calendarEventListTag,
      ],
    }),
    // #endregion

    // #region Remove Team Member
    /**
     * The client team leader takes a member off the vendor team or the client team of active work,
     * and the work goes on without them (#6070). Never a revision, so nothing comes back but a `204`.
     *
     * The agreement is asked for again rather than patched: the roster, the departures and -- for a
     * vendor member a rate named -- the fee schedule all change with it.
     */
    removeWorkTeamMember: build.mutation<
      null,
      { workId: string; version: number; side: WorkTeamSide; userId: string; sourceProposalId?: string }
    >({
      query: ({ workId, version, side, userId }) => ({
        url: `work/${workId}/teams/${side}/members/${userId}/remove`,
        method: "POST",
        body: {},
        headers: workAgreementVersionHeader(version),
      }),
      onQueryStarted: async (args, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          invalidateWorkAgreementListApiCaches(dispatch as AppDispatch);
          invalidateWorkAgreementByIdApiCache({ dispatch: dispatch as AppDispatch, workAgreementIdValue: args.workId });
          invalidateProposalsAWorkRosterChangeMoves({
            dispatch: dispatch as AppDispatch,
            sourceProposalIdValue: args.sourceProposalId,
          });
        } catch {
          // Failed mutations should leave the local work-agreement caches untouched.
        }
      },
    }),
    // #endregion

    // #region Team Leader Handover
    /**
     * A team leader offers to hand the team they lead over to somebody already on it (#5836). Answers
     * with the agreement, which carries the pending handover; nothing about the team changes until the
     * successor accepts.
     */
    requestTeamLeaderHandover: build.mutation<
      WorkAgreementJSON,
      {
        workAgreementId: string;
        version: number;
        successor: EntityMember;
        outgoingLeaderStays: boolean;
        grantSigningAuthority: boolean;
        revisionDisposition: InFlightRevisionDisposition;
      }
    >({
      query: ({ workAgreementId, version, successor, outgoingLeaderStays, grantSigningAuthority, revisionDisposition }) => ({
        url: `work/${workAgreementId}/teams/leader-handover`,
        method: "POST",
        body: {
          successor: { userId: successor.userId.value, entityId: successor.legalEntityId.value },
          outgoingLeaderStays,
          grantSigningAuthority,
          revisionDisposition,
        },
        headers: workAgreementVersionHeader(version),
      }),
      rawResponseSchema: WorkAgreementJSONZ,
      onSchemaFailure: logSchemaFailure,
      onQueryStarted: async (_args, { dispatch, getState, queryFulfilled }) => {
        const session = Session.loadFromStorage(() => {});
        try {
          const { data: workAgreementJSON } = await queryFulfilled;
          upsertWorkAgreementApiCaches({
            dispatch: dispatch as AppDispatch,
            getState: getState as () => RootState,
            session,
            workAgreementJSON,
          });
        } catch {
          // Failed mutations have no work agreement data to hydrate.
        }
      },
    }),

    /**
     * The successor accepts, and the handover applies. Nothing comes back -- a `204`, handed on as
     * `null` -- when the team simply has a new leader. A proposal comes back when the team was
     * restricted, or the outgoing leader who left held a rate, so the work ended and a continuation
     * the successor leads waits to be negotiated: the caller takes them to it.
     */
    acceptTeamLeaderHandover: build.mutation<ProposalJSON | null, { workAgreementId: string; version: number }>({
      query: ({ workAgreementId, version }) => ({
        url: `work/${workAgreementId}/teams/leader-handover/accept`,
        method: "POST",
        body: {},
        headers: workAgreementVersionHeader(version),
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, args) => [
        { type: "WorkAgreement", id: args.workAgreementId },
        { type: "WorkAgreement", id: "LIST" },
        { type: "Proposal", id: "LIST" },
      ],
    }),

    /** The successor says no. The leader keeps the team and is told. */
    declineTeamLeaderHandover: build.mutation<null, { workAgreementId: string; version: number }>({
      query: ({ workAgreementId, version }) => ({
        url: `work/${workAgreementId}/teams/leader-handover/decline`,
        method: "POST",
        body: {},
        headers: workAgreementVersionHeader(version),
      }),
      invalidatesTags: (_result, _error, args) => [{ type: "WorkAgreement", id: args.workAgreementId }],
    }),

    /** The leader takes back a handover nobody has answered. */
    withdrawTeamLeaderHandover: build.mutation<null, { workAgreementId: string; version: number }>({
      query: ({ workAgreementId, version }) => ({
        url: `work/${workAgreementId}/teams/leader-handover`,
        method: "DELETE",
        headers: workAgreementVersionHeader(version),
      }),
      invalidatesTags: (_result, _error, args) => [{ type: "WorkAgreement", id: args.workAgreementId }],
    }),
    // #endregion

    // #region Replace Vendor Team Members
    /**
     * Re-staffs a hired vendor team, in full: the roster sent is the roster the team ends up with,
     * so a member left out of it is a member removed.
     *
     * Answers in one of two shapes, and the caller has to handle both. Nothing comes back — a `204`,
     * which `fetchBaseQuery` hands on as `null` — when the roster simply changed on the live
     * agreement. A proposal comes back when the hired roster was a term of the agreement
     * (`WorkAgreement.vendorTeamRosterIsAnAgreedTerm`) and a revision was opened instead, which is
     * the same answer `work/{id}/teams/quit` gives for the same reason, and the caller must take
     * the user to it rather than report a quiet success.
     *
     * The leader is not part of the roster. Who speaks for the vendor is the party's standing on
     * the agreement, and moving it is a change to the terms — `revise` is the route for that.
     */
    replaceVendorTeamMembers: build.mutation<
      ProposalJSON | null,
      { workAgreementId: string; version: number; members: EntityMember[] }
    >({
      query: ({ workAgreementId, version, members }) => ({
        url: `work/${workAgreementId}/vendor-team-members`,
        method: "PUT",
        body: {
          members: members.map((member) => ({
            userId: member.userId.value,
            entityId: member.legalEntityId.value,
          })),
        },
        headers: workAgreementVersionHeader(version),
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ.nullable(),
      onSchemaFailure: logSchemaFailure,
      // The agreement itself is what the view reads the roster from, so its own tag is the one that
      // has to go: re-fetching it is what puts the new roster on the page. The proposal list goes
      // too because the other answer to this command is a revision that now belongs on it.
      invalidatesTags: (_result, _error, args) => [
        { type: "WorkAgreement", id: args.workAgreementId },
        { type: "Proposal", id: args.workAgreementId },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetWorkAgreementsQuery,
  useGetWorkAgreementByIdQuery,
  useGetWorkAgreementsByIdsQuery,
  useGetWorkTeamsByWorkAgreementIdQuery,
  useGetActiveWorkAgreementsQuery,
  useGetActiveWorkAgreementsCountQuery,
  useGetArchivedWorkAgreementsCountQuery,
  useGetWorkAgreementListCountsQuery,
  useEndWorkAgreementMutation,
  useReviseWorkAgreementMutation,
  useQuitWorkTeamMutation,
  useRemoveWorkTeamMemberMutation,
  useReplaceVendorTeamMembersMutation,
  useRequestTeamLeaderHandoverMutation,
  useAcceptTeamLeaderHandoverMutation,
  useDeclineTeamLeaderHandoverMutation,
  useWithdrawTeamLeaderHandoverMutation,
} = workAgreementsApi;

/** The work agreement list's counts, from its one counts request (#6709). */
export const useWorkAgreementListCounts = listCountsHook<{ criteria: string }, WorkAgreementListCounts>(
  useGetWorkAgreementListCountsQuery,
);
